import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../context/AppContext";
import AdminNavbar from "../components/AdminNavbar";
import toast from "react-hot-toast";

/* ── Section visual config (gradient + unit) — merged with backend data ── */
const SECTION_STYLE = {
  juices: {
    color: "linear-gradient(135deg,#2D8B4E,#4CAF72)",
    activeColor: "#2D8B4E",
    volumeUnit: "ml",
    volumePlaceholder: "e.g. 300ml",
  },
  salads: {
    color: "linear-gradient(135deg,#15803d,#22c55e)",
    activeColor: "#15803d",
    volumeUnit: "gms",
    volumePlaceholder: "e.g. 250gms",
  },
  sandwiches: {
    color: "linear-gradient(135deg,#b45309,#f59e0b)",
    activeColor: "#b45309",
    volumeUnit: "gms",
    volumePlaceholder: "e.g. 200gms",
  },
  smoothies: {
    color: "linear-gradient(135deg,#6d28d9,#a78bfa)",
    activeColor: "#6d28d9",
    volumeUnit: "ml",
    volumePlaceholder: "e.g. 350ml",
  },
};
const DEFAULT_STYLE = {
  color: "linear-gradient(135deg,#6b7280,#9ca3af)",
  activeColor: "#6b7280",
  volumeUnit: "gms",
  volumePlaceholder: "e.g. 200gms",
};

const blankForm = {
  name: "",
  description: "",
  price: "",
  originalPrice: "",
  category: "",
  volume: "",
  prepTime: "",
  stock: 30,
  isBestseller: false,
  isFeatured: false,
  isAvailable: true,
  type: "juice",
  section: "juices",
  ingredients: "",
  tags: "",
  calories: "",
  protein: "",
  carbs: "",
  fiber: "",
};

const Sec = ({ icon, title, color = "#2D8B4E" }) => (
  <div className="flex items-center gap-3 mb-4">
    <div
      className="w-9 h-9 rounded-xl flex items-center justify-center text-lg shrink-0"
      style={{ background: `${color}18`, color }}
    >
      {icon}
    </div>
    <h3 className="font-bold text-sm" style={{ color }}>
      {title}
    </h3>
  </div>
);

export default function AdminAddProduct() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const dropRef = useRef(null);

  const [sections, setSections] = useState([]);

  /* fetch sections from backend */
  useEffect(() => {
    api
      .get("/products/sections/all")
      .then((res) => {
        const raw = res.data?.sections || [];
        // enrich with style
        setSections(
          raw.map((s) => ({
            ...s,
            ...(SECTION_STYLE[s.key] || DEFAULT_STYLE),
          })),
        );
      })
      .catch(() => {});
  }, []);

  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [imgTab, setImgTab] = useState("upload");

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [cloudUrl, setCloudUrl] = useState("");
  const [urlInput, setUrlInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));
  const setChk = (f) => (e) =>
    setForm((p) => ({ ...p, [f]: e.target.checked }));
  const iCls = (f) =>
    `input-glass ${errors[f] ? "border-red-400 bg-red-50/40" : ""}`;

  /* current section config (from backend, enriched with style) */
  const activeSection = sections.find((s) => s.key === form.section) ||
    sections[0] || {
      key: "juices",
      volumeUnit: "ml",
      volumePlaceholder: "e.g. 300ml",
      ...DEFAULT_STYLE,
    };
  const unitColor = activeSection.activeColor || "#2D8B4E";

  /* ── file pick / drop ── */
  const acceptFile = useCallback((f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Max file size is 5 MB");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setCloudUrl("");
    setErrors((p) => ({ ...p, image: "" }));
  }, []);

  const onFilePick = (e) => acceptFile(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    acceptFile(e.dataTransfer.files?.[0]);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = () => setDragOver(false);

  /* ── Cloudinary upload ── */
  const uploadNow = useCallback(async () => {
    if (!file) return null;
    setUploading(true);
    setProgress(0);

    try {
      /* 1 — get signed credentials from backend */
      let signData;
      try {
        const { data } = await api.post("/products/cloudinary-sign");
        signData = data;
      } catch (err) {
        toast.error(
          err.response?.data?.message || "Could not get upload credentials",
        );
        return null;
      }

      const { cloudName, apiKey, signature, timestamp, folder } = signData;

      /* 2 — build FormData with ONLY the fields that were part of the signature
              Backend signs: folder=<folder>&timestamp=<ts>
              Any extra field here would break the signature. */
      const fd = new FormData();
      fd.append("file", file);
      fd.append("api_key", apiKey);
      fd.append("signature", signature);
      fd.append("timestamp", String(timestamp));
      fd.append("folder", folder); // must match exactly what backend signed

      /* 3 — POST directly to Cloudinary */
      const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;

      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.timeout = 60000;

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable)
            setProgress(Math.round((e.loaded / e.total) * 100));
        };

        xhr.onload = () => {
          if (xhr.status === 200) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch {
              reject(new Error("Invalid response from Cloudinary"));
            }
          } else {
            try {
              const errJson = JSON.parse(xhr.responseText);
              reject(
                new Error(
                  errJson?.error?.message ||
                    `Upload failed (HTTP ${xhr.status})`,
                ),
              );
            } catch {
              reject(new Error(`Upload failed — HTTP ${xhr.status}`));
            }
          }
        };

        xhr.onerror = () =>
          reject(new Error("Network error — check internet connection"));
        xhr.ontimeout = () => reject(new Error("Upload timed out after 60s"));

        xhr.open("POST", uploadUrl);
        xhr.send(fd);
      });

      setCloudUrl(result.secure_url);
      setProgress(100);
      toast.success("Image uploaded!");
      return result.secure_url;
    } catch (err) {
      toast.error("Upload failed: " + err.message);
      setProgress(0);
      return null;
    } finally {
      setUploading(false);
    }
  }, [file]);

  const clearImage = () => {
    setFile(null);
    setPreview("");
    setCloudUrl("");
    setProgress(0);
    if (fileRef.current) fileRef.current.value = "";
  };

  /* ── submit ── */
  const handleSubmit = async () => {
    setSaving(true);
    try {
      let finalImg = imgTab === "url" ? urlInput.trim() : cloudUrl;

      if (imgTab === "upload" && file && !cloudUrl) {
        finalImg = await uploadNow();
        if (!finalImg) {
          setSaving(false);
          return;
        }
      }

      const e = {};
      if (!form.name.trim()) e.name = "Required";
      if (!form.category) e.category = "Required";
      if (!form.price || +form.price <= 0) e.price = "Valid price required";
      if (!finalImg) e.image = "Upload an image or paste a URL";
      setErrors(e);
      if (Object.keys(e).length) {
        toast.error("Please fix the highlighted fields");
        setSaving(false);
        return;
      }

      await api.post("/products", {
        name: form.name.trim(),
        description: form.description.trim(),
        price: +form.price,
        originalPrice: form.originalPrice ? +form.originalPrice : null,
        category: form.category,
        image: finalImg,
        volume: form.volume,
        prepTime: form.prepTime,
        stock: +form.stock,
        isBestseller: form.isBestseller,
        isFeatured: form.isFeatured,
        isAvailable: form.isAvailable,
        type: form.type,
        ingredients: form.ingredients
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        tags: form.tags
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        nutritionInfo: {
          calories: form.calories ? +form.calories : undefined,
          protein: form.protein || undefined,
          carbs: form.carbs || undefined,
          fiber: form.fiber || undefined,
        },
      });

      setSuccess(true);
      toast.success("Product added!");
      setTimeout(() => {
        setForm(blankForm);
        clearImage();
        setUrlInput("");
        setSuccess(false);
      }, 2500);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add product");
    } finally {
      setSaving(false);
    }
  };

  const displayImg = imgTab === "url" ? urlInput : preview || cloudUrl;

  return (
    <div
      className="min-h-screen"
      style={{ background: "linear-gradient(135deg,#f0f9f0,#e8f5e9)" }}
    >
      <AdminNavbar />
      <div className="max-w-4xl mx-auto p-4 md:p-8">
        <div className="flex items-center gap-3 mb-8">
          <button
            onClick={() => navigate("/admin")}
            className="border border-green-500/30 text-green-600 rounded-full px-4 py-1.5 text-sm font-semibold hover:bg-green-500/10 bg-white/60"
          >
            ← Back
          </button>
          <div>
            <h1 className="text-3xl font-black text-green-700">
              Add New Product
            </h1>
            <p className="text-gray-400 text-sm">
              Fill in the details to add a product to the catalogue
            </p>
          </div>
        </div>

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 rounded-2xl p-4 flex items-center gap-2 font-semibold">
            ✅ Product added successfully! Form has been reset.
          </div>
        )}

        <div className="grid md:grid-cols-5 gap-6">
          {/* ── LEFT ── */}
          <div className="md:col-span-3 flex flex-col gap-5">
            {/* Basic info */}
            <div className="glass-card p-5">
              <Sec icon="🥤" title="Basic Information" />
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Name */}
                <div className="sm:col-span-2">
                  <label className="label-xs">Product Name *</label>
                  <input
                    className={iCls("name")}
                    value={form.name}
                    onChange={set("name")}
                    placeholder="e.g. Mango Burst"
                  />
                  {errors.name && <p className="err">{errors.name}</p>}
                </div>

                {/* Description */}
                <div className="sm:col-span-2">
                  <label className="label-xs">Description</label>
                  <textarea
                    className="input-glass h-24 resize-none"
                    value={form.description}
                    onChange={set("description")}
                    placeholder="Describe the product — ingredients, taste, benefits…"
                  />
                </div>

                {/* Section + Category */}
                <div>
                  <label className="label-xs">Section</label>
                  {sections.length === 0 ? (
                    <div className="text-xs text-gray-400 py-2">
                      Loading sections…
                    </div>
                  ) : (
                    <div className="flex gap-2 mt-1 mb-3 flex-wrap">
                      {sections.map((s) => (
                        <button
                          key={s.key}
                          type="button"
                          onClick={() =>
                            setForm((p) => ({
                              ...p,
                              section: s.key,
                              category: "",
                              type:
                                s.key === "juices" || s.key === "smoothies"
                                  ? "juice"
                                  : "food",
                              volume: "",
                            }))
                          }
                          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border-2 transition-all"
                          style={{
                            background:
                              form.section === s.key ? s.color : "white",
                            color: form.section === s.key ? "white" : "#9ca3af",
                            borderColor:
                              form.section === s.key
                                ? "transparent"
                                : "#e5e7eb",
                            boxShadow:
                              form.section === s.key
                                ? `0 4px 14px ${s.activeColor}40`
                                : "none",
                          }}
                        >
                          {s.emoji} {s.label}
                        </button>
                      ))}
                    </div>
                  )}

                  <label className="label-xs">Category *</label>
                  <select
                    className={iCls("category")}
                    value={form.category}
                    onChange={set("category")}
                  >
                    <option value="">Select Category</option>
                    {(activeSection?.categories || []).map((c) => (
                      <option key={c.key || c.value} value={c.key || c.value}>
                        {c.emoji} {c.label}
                      </option>
                    ))}
                  </select>
                  {errors.category && <p className="err">{errors.category}</p>}
                </div>

                {/* Volume + Prep Time */}
                <div>
                  {/* Volume — unit label switches by section */}
                  <label className="label-xs">
                    Volume / Size
                    <span
                      style={{
                        marginLeft: 6,
                        padding: "1px 6px",
                        borderRadius: 4,
                        fontSize: 10,
                        fontWeight: 800,
                        letterSpacing: "0.3px",
                        background: `${unitColor}18`,
                        color: unitColor,
                      }}
                    >
                      {activeSection.volumeUnit}
                    </span>
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      className="input-glass"
                      value={form.volume}
                      onChange={set("volume")}
                      placeholder={activeSection.volumePlaceholder}
                      style={{ paddingRight: 44 }}
                    />
                    <span
                      style={{
                        position: "absolute",
                        right: 12,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: 11,
                        fontWeight: 800,
                        color: unitColor,
                        pointerEvents: "none",
                      }}
                    >
                      {activeSection.volumeUnit}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {activeSection.key === "salads" ||
                    activeSection.key === "sandwiches"
                      ? "Weight in grams"
                      : "Volume in millilitres"}
                  </p>

                  <label className="label-xs mt-3">Prep Time</label>
                  <input
                    className="input-glass mt-1"
                    value={form.prepTime}
                    onChange={set("prepTime")}
                    placeholder="5 min"
                  />
                </div>
              </div>
            </div>

            {/* Pricing & Stock */}
            <div className="glass-card p-5">
              <Sec icon="💰" title="Pricing & Stock" color="#FF6B35" />
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="label-xs">Price (₹) *</label>
                  <input
                    className={iCls("price")}
                    type="number"
                    value={form.price}
                    onChange={set("price")}
                    placeholder="99"
                  />
                  {errors.price && <p className="err">{errors.price}</p>}
                </div>
                <div>
                  <label className="label-xs">Original Price</label>
                  <input
                    className="input-glass"
                    type="number"
                    value={form.originalPrice}
                    onChange={set("originalPrice")}
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="label-xs">Stock</label>
                  <input
                    className="input-glass"
                    type="number"
                    value={form.stock}
                    onChange={set("stock")}
                  />
                </div>
              </div>
            </div>

            {/* Ingredients & Tags */}
            <div className="glass-card p-5">
              <Sec icon="💧" title="Ingredients & Tags" color="#9C27B0" />
              <div className="flex flex-col gap-4">
                <div>
                  <label className="label-xs">
                    Ingredients (comma-separated)
                  </label>
                  <input
                    className="input-glass"
                    value={form.ingredients}
                    onChange={set("ingredients")}
                    placeholder={
                      activeSection.key === "salads" ||
                      activeSection.key === "sandwiches"
                        ? "Lettuce, Tomato, Cheese"
                        : "Mango, Himalayan Salt, Lemon"
                    }
                  />
                </div>
                <div>
                  <label className="label-xs">Tags (comma-separated)</label>
                  <input
                    className="input-glass"
                    value={form.tags}
                    onChange={set("tags")}
                    placeholder="healthy, fresh, natural"
                  />
                </div>
              </div>
            </div>

            {/* Nutrition */}
            <div className="glass-card p-5">
              <Sec
                icon="📊"
                title="Nutrition Info (optional)"
                color="#2196F3"
              />
              <div className="grid grid-cols-4 gap-3">
                {[
                  { l: "Calories", f: "calories", t: "number" },
                  { l: "Protein", f: "protein", ph: "1.5g" },
                  { l: "Carbs", f: "carbs", ph: "28g" },
                  { l: "Fiber", f: "fiber", ph: "2g" },
                ].map(({ l, f, t, ph }) => (
                  <div key={f}>
                    <label className="label-xs">{l}</label>
                    <input
                      className="input-glass"
                      type={t || "text"}
                      value={form[f]}
                      onChange={set(f)}
                      placeholder={ph}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── RIGHT ── */}
          <div className="md:col-span-2">
            <div className="glass-card p-5 sticky top-20 flex flex-col gap-5">
              {/* Image */}
              <div>
                <Sec icon="🖼️" title="Product Image" />

                {/* tab switcher */}
                <div
                  className="flex gap-1 p-1 rounded-xl mb-4"
                  style={{
                    background: "rgba(45,139,78,0.07)",
                    border: "1.5px solid rgba(45,139,78,0.12)",
                  }}
                >
                  {[
                    { k: "upload", l: "📁 Upload" },
                    { k: "url", l: "🔗 URL" },
                  ].map((m) => (
                    <button
                      key={m.k}
                      onClick={() => setImgTab(m.k)}
                      className="flex-1 py-1.5 rounded-lg text-xs font-bold transition-all"
                      style={{
                        background:
                          imgTab === m.k
                            ? "linear-gradient(135deg,#2D8B4E,#4CAF72)"
                            : "transparent",
                        color: imgTab === m.k ? "white" : "#9ca3af",
                      }}
                    >
                      {m.l}
                    </button>
                  ))}
                </div>

                {imgTab === "upload" ? (
                  <div>
                    {/* drop zone */}
                    {!preview && !cloudUrl ? (
                      <div
                        ref={dropRef}
                        onClick={() => fileRef.current?.click()}
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        onDragLeave={onDragLeave}
                        className="border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all"
                        style={{
                          borderColor: dragOver ? "#2D8B4E" : "#bbf7d0",
                          background: dragOver
                            ? "rgba(45,139,78,0.04)"
                            : "transparent",
                        }}
                      >
                        <div className="text-4xl mb-2">📸</div>
                        <p className="text-sm font-bold text-gray-600">
                          Click or drag &amp; drop
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          PNG · JPG · WEBP — max 5 MB
                        </p>
                      </div>
                    ) : (
                      <div className="relative">
                        <img
                          src={preview || cloudUrl}
                          alt="preview"
                          className="w-full h-40 object-cover rounded-2xl"
                        />
                        <button
                          onClick={clearImage}
                          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 text-white text-xs hover:bg-red-500 transition-colors flex items-center justify-center font-bold"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={onFilePick}
                    />

                    {/* upload button / progress */}
                    {file && !cloudUrl && (
                      <div className="mt-3">
                        <p className="text-xs text-gray-500 font-semibold mb-2 truncate">
                          📄 {file.name}
                        </p>
                        {uploading ? (
                          <div>
                            <div className="w-full bg-gray-100 rounded-full h-1.5 mb-1">
                              <div
                                className="h-1.5 rounded-full transition-all duration-300"
                                style={{
                                  width: `${progress}%`,
                                  background:
                                    "linear-gradient(90deg,#2D8B4E,#4CAF72)",
                                }}
                              />
                            </div>
                            <p className="text-xs text-green-600 font-bold">
                              {progress}% uploading…
                            </p>
                          </div>
                        ) : (
                          <button
                            onClick={uploadNow}
                            className="w-full py-2 rounded-xl text-sm font-bold text-white"
                            style={{
                              background:
                                "linear-gradient(135deg,#2D8B4E,#4CAF72)",
                              boxShadow: "0 3px 12px rgba(45,139,78,0.3)",
                            }}
                          >
                            ☁️ Upload to Cloudinary
                          </button>
                        )}
                      </div>
                    )}

                    {cloudUrl && (
                      <div className="mt-2 flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl p-2.5">
                        <span className="text-green-500">✅</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-green-700">
                            Saved to Cloudinary
                          </p>
                          <p className="text-xs text-gray-400 truncate">
                            {cloudUrl}
                          </p>
                        </div>
                        <button
                          onClick={clearImage}
                          className="text-gray-400 hover:text-red-500 text-xs shrink-0"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="label-xs">Image URL *</label>
                    <input
                      className={iCls("image")}
                      value={urlInput}
                      onChange={(e) => setUrlInput(e.target.value)}
                      placeholder="https://images.unsplash.com/…"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Paste any direct image URL
                    </p>
                    {urlInput && (
                      <div className="mt-3 rounded-2xl overflow-hidden h-36">
                        <img
                          src={urlInput}
                          alt="preview"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
                {errors.image && (
                  <p className="text-xs text-red-500 mt-2">⚠️ {errors.image}</p>
                )}
              </div>

              {/* Flags */}
              <div>
                <Sec icon="🚩" title="Product Flags" color="#FF6B35" />
                {[
                  {
                    label: "Bestseller",
                    field: "isBestseller",
                    desc: "Show bestseller badge",
                  },
                  {
                    label: "Featured",
                    field: "isFeatured",
                    desc: "Show on homepage",
                  },
                  {
                    label: "Available",
                    field: "isAvailable",
                    desc: "Visible to customers",
                  },
                ].map(({ label, field, desc }) => (
                  <label
                    key={field}
                    className="flex justify-between items-center p-3 rounded-xl bg-green-500/5 border border-green-500/10 mb-2 cursor-pointer"
                  >
                    <div>
                      <p className="text-sm font-semibold">{label}</p>
                      <p className="text-xs text-gray-400">{desc}</p>
                    </div>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={form[field]}
                        onChange={setChk(field)}
                        className="sr-only peer"
                      />
                      <div className="w-10 h-6 rounded-full bg-gray-200 peer-checked:bg-green-500 transition-colors" />
                      <div className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
                    </div>
                  </label>
                ))}
              </div>

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={saving || uploading}
                className="w-full py-4 text-base font-extrabold rounded-2xl flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
                style={{
                  background: "linear-gradient(135deg,#2D8B4E,#4CAF72)",
                  color: "white",
                  boxShadow: "0 8px 30px rgba(45,139,78,0.4)",
                }}
              >
                {saving ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{" "}
                    Adding…
                  </>
                ) : (
                  "➕ Add Product"
                )}
              </button>
              <button
                onClick={() => navigate("/admin/inventory")}
                className="btn-outline-green w-full py-2.5 text-sm"
              >
                View Inventory
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .label-xs { display:block; font-size:11px; font-weight:700; color:#6b7280; margin-bottom:4px; text-transform:uppercase; letter-spacing:.4px; }
        .err { font-size:11px; color:#ef4444; margin-top:3px; }
      `}</style>
    </div>
  );
}
